import { useEffect, useState } from 'react'

import { getRouteApi, notFound } from '@tanstack/react-router'
import { BSA_CTR_PATH } from 'commons/constants/routes'
import { ClampedText } from 'ui/components'
import { ButtonGroup } from 'ui/components/ButtonGroup'
import { Button, IconButton } from 'ui/components/Buttons'
import { IconName } from 'ui/components/Icon'
import { Page, PageFace } from 'ui/components/Page'
import { useConfirmation } from 'ui/hooks'
import { CTRSideBar } from '~bank-bsa-reporting/components'
import { CTR } from '~bank-bsa-reporting/hooks'
import { useGetCTRDetails } from '~bank-bsa-reporting/hooks/ctr/useGetCTRDetails'

import { Transactions } from '../components'
import {
  AmountAndTypeOfTransactions,
  FilingInstitutionContactInformation,
  FincenWarnings,
  Footer,
  GeneralInformation,
  History,
  PersonsInvolvedInformation,
  TransactionsLocationsInformation,
  Warnings
} from './components'

const routeApi = getRouteApi('/_app/bsa/ctrs/$id')

const Details = () => {
  const params = routeApi.useParams()
  const navigate = routeApi.useNavigate()

  const [open, setOpen] = useState(false)
  const deleteMutation = CTR.useDelete()

  const { data, isFetching, historyTableConfig } = useGetCTRDetails(+params.id)

  const { nextId, previousId, id, generalInfo, canApprove, canCancel } = data || {}

  const { nameDesc } = generalInfo || {}

  useEffect(() => {
    if ((!isFetching && !data) || +params.id === 0 || isNaN(+params.id)) {
      throw notFound()
    }
  }, [isFetching, data, params.id])

  const { open: startDeleting, Confirmation } = useConfirmation({
    message: `Are you sure you want to delete ${nameDesc} CTR?`,
    onConfirm: () =>
      deleteMutation.mutateAsync([id || 0]).then(() => {
        navigate({ to: BSA_CTR_PATH })
      }),
    confirmationButtonText: 'Delete',
    isConfirmationNegative: true
  })

  return (
    <Page
      face={PageFace.SECONDARY}
      title={<ClampedText>{nameDesc}</ClampedText>}
      subTitle="CTR"
      actions={
        <ButtonGroup margin="big">
          <IconButton data-qa="Delete CTR" onClick={startDeleting} icon={IconName.DELETE} face="neutral">
            Delete CTR
          </IconButton>
          <Button onClick={() => setOpen(true)} face="inherit white">
            Transactions
          </Button>
        </ButtonGroup>
      }
      footer={
        <Footer
          id={id || 0}
          nextId={nextId}
          previousId={previousId}
          showApprove={!!canApprove}
          showCancel={!!canCancel}
        />
      }
    >
      <Confirmation />
      {!!data && (
        <>
          <CTRSideBar />
          {open && <Transactions onClose={() => setOpen(false)} transactions={data.transactions} ctrId={data.id} />}
          {data.attention.length > 0 && <Warnings attention={data.attention} />}
          {data.fincenWarnings.length > 0 && <FincenWarnings warnings={data.fincenWarnings} />}
          <GeneralInformation data={data} />
          <FilingInstitutionContactInformation data={data} />
          <TransactionsLocationsInformation data={data} />
          <PersonsInvolvedInformation data={data} />
          <AmountAndTypeOfTransactions data={data} />
          <History
            config={historyTableConfig}
            initialCompanyHistory={data.history}
            initialCompanyHistoryTotalCount={data.historyTotalCount}
          />
        </>
      )}
    </Page>
  )
}

export default Details
